import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';

/**
 * Message to show for a failed Microsoft sign-in, or an empty string when the
 * person simply closed the window, which is not an error they need told about.
 *
 * @param {{ code?: string }} err - Error thrown by the Firebase SDK.
 * @returns {string}
 */
export function microsoftSignInErrorMessage(err) {
    switch (err?.code) {
        case 'auth/popup-closed-by-user':
        case 'auth/cancelled-popup-request':
        case 'auth/user-cancelled':
            return '';
        case 'auth/popup-blocked':
            return 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.';
        case 'auth/account-exists-with-different-credential':
            return 'An account with this email already uses a password. Log in with your email and password instead.';
        case 'auth/missing-email':
            return 'Your Microsoft account did not share an email address, which the Hub needs to identify you. Ask your account administrator, or sign in with email and password.';
        case 'auth/operation-not-allowed':
            return 'Microsoft sign-in is not enabled for this environment.';
        case 'auth/unauthorized-domain':
            return 'This site is not authorized for Microsoft sign-in.';
        case 'auth/network-request-failed':
            return 'Network error. Check your connection and try again.';
        default:
            return 'Microsoft sign-in failed. Please try again.';
    }
}

function MicrosoftLogo() {
    // Microsoft's four-square mark, drawn inline so nothing is fetched.
    return (
        <svg
            width="18"
            height="18"
            viewBox="0 0 21 21"
            aria-hidden="true"
            focusable="false"
        >
            <rect x="1" y="1" width="9" height="9" fill="#f25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
            <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
        </svg>
    );
}

/**
 * Starts a Microsoft (Entra ID) sign-in and navigates on success.
 *
 * @param {object} props
 * @param {string} props.label - Button text.
 * @param {string} props.redirectTo - Route to open after sign-in.
 * @param {(message: string) => void} props.onError - Receives a message to
 *   show, or an empty string to clear one.
 * @param {(request: { email: string, pendingCredential: object }) => void}
 *   [props.onLinkRequired] - Called instead of onError when the address
 *   already belongs to a password account, with what the page needs to link
 *   the two. Without it the person is told to use their password.
 * @param {boolean} [props.disabled] - Disable while the surrounding form is
 *   busy.
 */
function MicrosoftSignInButton({
    label,
    redirectTo,
    onError,
    onLinkRequired,
    disabled,
}) {
    const [busy, setBusy] = useState(false);
    const { loginWithMicrosoft } = useAuth();
    const navigate = useNavigate();

    async function handleClick() {
        try {
            onError('');
            setBusy(true);
            await loginWithMicrosoft();
            navigate(redirectTo, { replace: true });
        } catch (err) {
            if (
                err?.code === 'auth/account-exists-with-different-credential' &&
                err.pendingCredential &&
                err.email &&
                onLinkRequired
            ) {
                onLinkRequired({
                    email: err.email,
                    pendingCredential: err.pendingCredential,
                });
                return;
            }
            const message = microsoftSignInErrorMessage(err);
            if (message) {
                onError(message);
            }
        } finally {
            setBusy(false);
        }
    }

    return (
        <Button
            type="button"
            variant="outline-dark"
            className="w-100 d-flex align-items-center justify-content-center gap-2"
            onClick={handleClick}
            disabled={disabled || busy}
            aria-busy={busy}
        >
            <MicrosoftLogo />
            <span>{busy ? 'Opening Microsoft sign-in...' : label}</span>
        </Button>
    );
}

export default MicrosoftSignInButton;
